/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { DirectionalHint } from '@fluentui/react/lib/Callout';
import { Link } from '@fluentui/react/lib/Link';
import { IOverflowSetItemProps, IOverflowSetProps, IOverflowSetStyles, OverflowSet } from '@fluentui/react/lib/OverflowSet';
import {
    IPersonaCoinStyleProps,
    IPersonaCoinStyles,
    IPersonaProps,
    IPersonaStyles,
    Persona,
    PersonaSize
} from '@fluentui/react/lib/Persona';
import { IStackItemStyles, IStackProps, Stack } from '@fluentui/react/lib/Stack';
import { TeachingBubble } from '@fluentui/react/lib/TeachingBubble';
import { classNamesFunction, getId } from '@fluentui/react/lib/Utilities';
import { observer } from 'mobx-react';
import * as React from 'react';
import { defineMessages } from 'react-intl';
import { useHistory } from 'react-router';
import { GlobalHeaderBlades } from 'src/appShell/globalHeader/globalHeaderBlades/GlobalHeaderBlades';
import {
    onRenderLoggedInHeaderFarItem,
    onRenderLoggedInHeaderItem,
    onRenderLoggedInHeaderMoreButton
} from 'src/appShell/globalHeader/loggedInHeader/renderItems';
import { GlobalHeaderBladeType } from 'src/appShell/globalHeader/types';
import { GlobalHeaderLinks } from 'src/appShell/globalHeader/utils/globalHeaderLinks';
import { LuisText } from 'src/core/components/LuisText';
import { useGlobalState } from 'src/core/hooks/useGlobalState';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { useResponsiveMode } from 'src/core/hooks/useResponsiveMode';
import { logCustomerFeedbackSmileyClick, logSignIn } from 'src/core/services/telemetry/eventsNamesTemplate';
import { getScopedTheme, getStylistV2 } from 'src/core/themes/luisStylist';
import { routeTable } from 'src/core/utils/routeTable';
import { getPortalFromUrl } from 'src/core/utils/routingUtils';
import { Toaster } from 'src/luis/projects/events/components/Toaster/Toaster';
import styled from 'styled-components';
import { LinksKind, LinksMenu } from './LinksMenu';

const messages = defineMessages({
    ok: { id: 'portal.loggedInHeader.ok', defaultMessage: 'Ok' },
    or: { id: 'portal.loggedInHeader.or', defaultMessage: 'or' },
    signIn: { id: 'portal.loggedInHeader.signIn', defaultMessage: 'Sign in' },
    helpButton: { id: 'portal.loggedInHeader.helpButton', defaultMessage: 'Help' },
    settingsButton: { id: 'portal.loggedInHeader.settingsButton', defaultMessage: 'Settings' },
    avatarButton: { id: 'portal.loggedInHeader.avatarButton', defaultMessage: 'User profile' },
    feedbackButton: { id: 'portal.loggedInHeader.feedbackButton', defaultMessage: 'Feedback' },
    toastsButton: { id: 'portal.loggedInHeader.toastsButton', defaultMessage: 'Notifications' },
    dismissButtonAria: { id: 'portal.loggedInHeader.dismissButtonAria', defaultMessage: 'Close' },
    signUp: { id: 'portal.loggedInHeader.signUp', defaultMessage: 'Sign up and get free $200 Azure Credit' },
    createAzureAccount: { id: 'portal.loggedInHeader.createAzureAccount', defaultMessage: 'Create one here' },
    noResourceBubbleHeader: { id: 'portal.loggedInHeader.noResourceBubbleHeader', defaultMessage: 'Select an Azure resource' },
    noResourceBubbleDescription: {
        id: 'portal.loggedInHeader.noResourceBubbleDescription',
        defaultMessage: `In order to get the most of Language Studio, you will need to
        select an Azure resource. You can select your resource under the user profile.`
    },
    signInTeachingBubbleTitle: {
        id: 'portal.loggedInHeader.signInTeachingBubbleTitle',
        defaultMessage: 'Sign in with Azure to get full access to Language Studio'
    },
    signInTeachingBubbleDescription: {
        id: 'portal.loggedInHeader.signInTeachingBubbleDescription',
        defaultMessage:
            'You can explore and try out Language services without signing in. To get full access to Language Studio, please sign in with your Azure account.'
    },
    signInTeachingBubbleDescription2: {
        id: 'portal.loggedInHeader.signInTeachingBubbleDescription2',
        defaultMessage: `Don't have Azure account?`
    },
    learnHowToCreateAzureAccount: {
        id: 'portal.loggedInHeader.learnHowToCreateAzureAccount',
        defaultMessage: 'Learn how to create Azure account'
    },
    learnMoreAboutAzureAccount: {
        id: 'portal.loggedInHeader.learnMoreAboutAzureAccount',
        defaultMessage: 'Learn more about Azure account'
    }
});

const headerTheme = getScopedTheme('LoggedInHeader');
const { styleSpan } = getStylistV2('LoggedInHeader');

const RegionText = styleSpan('RegionText', { fontSize: 11 });

const headerStyles = classNamesFunction<IStackProps, IStackItemStyles>()({
    root: {
        minHeight: headerTheme.maxHeight,
        maxHeight: headerTheme.maxHeight,
        padding: headerTheme.horizontalPadding,
        backgroundColor: headerTheme.backgroundColor
    }
});
const personaStyles = classNamesFunction<IPersonaProps, IPersonaStyles>()({
    details: { height: '100%' },
    primaryText: { height: '100%' },
    root: { padding: headerTheme.avatarPadding, direction: 'rtl', height: '100%' }
});
const personaCoinStyles = classNamesFunction<IPersonaCoinStyleProps, IPersonaCoinStyles>()({
    initials: { color: headerTheme.avatarForegroundColor, fontWeight: 'bold' }
});
const overflowSetStyles = classNamesFunction<IOverflowSetProps, IOverflowSetStyles>()({ root: { height: '100%' } });

const avatarItemKey = 'avatar';
const serviceItemKey = 'serviceName';
const languageStudioKey = 'languageStudio';
const languageStudioSeparatorKey = 'languageStudioSeparator';
const studioSwitcherKey = 'studioSwitcher';
const languageStudioKeys = [languageStudioKey, languageStudioSeparatorKey, studioSwitcherKey];

const TeachingBubbleLink = styled(Link)<{ $isLearnMore: boolean }>(props => ({
    fontWeight: 500,
    color: `${headerTheme.textColor} !important`,
    marginBottom: props.$isLearnMore ? '15px' : '0px',
    display: props.$isLearnMore ? 'block' : 'inline-block'
}));

/**
 * Renders the header displayed in the logged in state.
 */
export const LoggedInHeader = observer(() => {
    const history = useHistory();
    const { localize } = useLocalization();
    const responsiveMode = useResponsiveMode();
    const { uiStore, rootStore, dispatcher } = useGlobalState();

    const personaId = React.useRef(getId('personaId')).current;
    const signInButtonId = React.useRef(getId('signInButtonId')).current;

    const user = rootStore.userStore;
    const { isSignedIn } = rootStore.authStore;
    const isProfileMenuOpen = uiStore.currentActiveGlobalBlade === 'profile';
    const isBladeOpen = uiStore.currentActiveGlobalBlade !== null && !isProfileMenuOpen;

    const { csAccountStore: selectedCsAccount } = rootStore.azureStore.session;
    const unseenNotificationsCount = rootStore.toastStoreHouse.unseenToastsCount;
    const isLuisPortal = uiStore.portal.kind === 'luis' || uiStore.portal.subPath === 'luis';
    const areNotificationsInProgress = rootStore.toastStoreHouse.items.some(n => n.status === 'running');

    const handleLanguageStudioClick = () => {
        toggleActiveBlade(null);
        history.push(routeTable.languageStudio.root());
    };

    const signIn = React.useCallback(() => {
        logSignIn();
        dispatcher.dispatch('login');
    }, [dispatcher]);

    const linksMenuItems = [
        LinksKind.AzureMachineLearning,
        LinksKind.AzureAIStudio,
        LinksKind.ContentSafety,
        LinksKind.CustomTranslator,
        LinksKind.DocumentIntelligence,
        LinksKind.VisionStudio,
        LinksKind.LanguageStudio,
        LinksKind.SpeechStudio
    ];

    const items: IOverflowSetItemProps[] = [
        {
            key: studioSwitcherKey,
            text: <LinksMenu items={linksMenuItems} />
        },
        {
            target: '_blank',
            key: 'cognitiveServices',
            text: 'Azure AI',
            rel: 'noopener noreferrer',
            href: GlobalHeaderLinks.cognitiveServicesLink
        },
        {
            key: languageStudioSeparatorKey,
            text: '|'
        },
        { key: languageStudioKey, text: 'Language Studio', onClick: handleLanguageStudioClick },
        ...(isSignedIn && isLuisPortal
            ? [
                  {
                      key: serviceItemKey,
                      text: (
                          <>
                              Language Understanding
                              {selectedCsAccount?.region && (
                                  <RegionText>
                                      {' - '}
                                      {selectedCsAccount.region}
                                  </RegionText>
                              )}
                          </>
                      ),
                      onClick: () => history.push(routeTable.luis.root())
                  }
              ]
            : [])
    ];

    // always display the Azure AI and Language studio texts on the menu bar - 25862367
    const responsiveItems = items.filter(item =>
        uiStore.portal.kind === 'languageStudio' ? true : languageStudioKeys.every(key => item.key !== key)
    );

    const toggleActiveBlade = (bladeType: GlobalHeaderBladeType) => {
        const currentActiveBlade = uiStore.currentActiveGlobalBlade;
        dispatcher.dispatch('updateActiveGlobalBlade', { newBlade: currentActiveBlade === bladeType ? null : bladeType });
    };

    /**
     * The callout for the profile information dismisses when
     * clicked outside. This dismissal is uncontrolled, thus
     * this timeout is necessary to avoid conflict of state with
     * the toggle when clicking on the avatar button in the header,
     * which is considered outside of the callout, but also toggles
     * the profile state.
     */

    const onProfileCalloutDismiss = () => {
        setTimeout(() => {
            if (uiStore.currentActiveGlobalBlade === 'profile') {
                toggleActiveBlade('profile');
            }
        }, 100);
    };

    const onFeddbackClick = () => {
        logCustomerFeedbackSmileyClick(getPortalFromUrl().serviceName);
        toggleActiveBlade('feedback');
    };

    const getFarOverFlowItems = (): { items: IOverflowSetItemProps[]; farItems: IOverflowSetItemProps[] } => {
        const getButtonText = (message: string) => (!responsiveMode.isXLargeOrGreater ? message : undefined);
        const farItems = isSignedIn
            ? [
                  {
                      key: 'toasts',
                      notification: true,
                      onClick: () => {
                          dispatcher.dispatch('markAllToastsAsSeen');
                          toggleActiveBlade('toasts');
                      },
                      count: unseenNotificationsCount,
                      iconProps: { iconName: 'Ringer' },
                      content: localize(messages.toastsButton),
                      isInProgress: areNotificationsInProgress,
                      ariaLabel: localize(messages.toastsButton),
                      text: getButtonText(localize(messages.toastsButton))
                  },
                  {
                      key: 'feedback',
                      iconProps: { iconName: 'Emoji2' },
                      content: localize(messages.feedbackButton),
                      ariaLabel: localize(messages.feedbackButton),
                      onClick: onFeddbackClick,
                      text: getButtonText(localize(messages.feedbackButton))
                  },
                  {
                      key: 'settings',
                      iconProps: { iconName: 'Settings' },
                      content: localize(messages.settingsButton),
                      ariaLabel: localize(messages.settingsButton),
                      onClick: () => toggleActiveBlade('settings'),
                      text: getButtonText(localize(messages.settingsButton))
                  },
                  {
                      key: 'help',
                      iconProps: { iconName: 'Help' },
                      content: localize(messages.helpButton),
                      ariaLabel: localize(messages.helpButton),
                      onClick: () => toggleActiveBlade('help'),
                      text: getButtonText(localize(messages.helpButton))
                  },
                  {
                      isProfileMenuOpen,
                      key: avatarItemKey,
                      onProfileCalloutDismiss,
                      content: localize(messages.avatarButton),
                      ariaLabel: localize(messages.avatarButton),
                      onClick: () => uiStore.currentActiveGlobalBlade !== 'profile' && toggleActiveBlade('profile'),
                      onRenderChildren: () => (
                          <Persona
                              id={personaId}
                              styles={personaStyles}
                              size={PersonaSize.size28}
                              imageInitials={user.initials}
                              coinProps={{ styles: personaCoinStyles }}
                              initialsColor={headerTheme.avatarBackgroundColor}
                              imageUrl={`data:image/jpeg;base64, ${user.photo}`}
                              onRenderPrimaryText={() => (
                                  <Stack verticalFill verticalAlign="center" horizontalAlign="end">
                                      <LuisText fontWeight={600} data-automation-id="userEmail" color={headerTheme.textColor}>
                                          {user.email}
                                      </LuisText>

                                      {selectedCsAccount && (
                                          <LuisText data-automation-id="csAccountName" color={headerTheme.textColor} variant="xSmall">
                                              {selectedCsAccount.name} ({selectedCsAccount.region}, {selectedCsAccount.sku.code})
                                          </LuisText>
                                      )}
                                  </Stack>
                              )}
                          />
                      )
                  }
              ]
            : [
                  {
                      key: 'settings',
                      iconProps: { iconName: 'Settings' },
                      content: localize(messages.settingsButton),
                      ariaLabel: localize(messages.settingsButton),
                      onClick: () => toggleActiveBlade('settings'),
                      text: getButtonText(localize(messages.settingsButton))
                  },
                  {
                      key: 'help',
                      iconProps: { iconName: 'Help' },
                      content: localize(messages.helpButton),
                      ariaLabel: localize(messages.helpButton),
                      onClick: () => toggleActiveBlade('help'),
                      text: getButtonText(localize(messages.helpButton))
                  },
                  {
                      iconProps: null,
                      onClick: signIn,
                      key: signInButtonId,
                      text: localize(messages.signIn),
                      content: localize(messages.signIn),
                      ariaLabel: localize(messages.signIn)
                  }
              ];

        if (responsiveMode.isXLargeOrGreater) {
            return { items: farItems, farItems: [] };
        }
        return {
            items: farItems.filter(item => item.key === avatarItemKey),
            farItems: farItems.filter(item => item.key !== avatarItemKey)
        };
    };

    const onLastBladeDismissed = () => {
        dispatcher.dispatch('updateActiveGlobalBlade', { newBlade: null });
    };

    return (
        <>
            <Stack
                as="nav"
                horizontal
                role="menu"
                styles={headerStyles}
                verticalAlign="center"
                tokens={{ childrenGap: 15 }}
                horizontalAlign="space-between">
                <Stack data-automation-id="itemsContainer" verticalFill horizontal verticalAlign="center">
                    {responsiveItems.map(onRenderLoggedInHeaderItem)}
                </Stack>

                <OverflowSet
                    role="menu"
                    overflowSide="start"
                    styles={overflowSetStyles}
                    items={getFarOverFlowItems().items}
                    overflowItems={getFarOverFlowItems().farItems}
                    onRenderOverflowButton={itemsToRender => onRenderLoggedInHeaderMoreButton(itemsToRender, localize)}
                    onRenderItem={item => onRenderLoggedInHeaderFarItem(item, uiStore.currentActiveGlobalBlade, localize)}
                />
            </Stack>

            <GlobalHeaderBlades
                isOpen={isBladeOpen}
                onLastBladeDismissed={onLastBladeDismissed}
                bladeType={uiStore.currentActiveGlobalBlade}
            />

            <Toaster quietAllToasts={uiStore.currentActiveGlobalBlade === 'toasts'} />

            {isSignedIn && !uiStore.isNoAccountTeachingBubbleHidden && (
                <TeachingBubble
                    hasCloseButton
                    target={`#${personaId}`}
                    headline={localize(messages.noResourceBubbleHeader)}
                    calloutProps={{ preventDismissOnScroll: true, directionalHint: DirectionalHint.bottomAutoEdge }}
                    onDismiss={() => dispatcher.dispatch('updateCsAccountTeachingBubbleVisibility', { isVisible: false })}
                    primaryButtonProps={{
                        children: localize(messages.ok),
                        onClick: () => dispatcher.dispatch('updateCsAccountTeachingBubbleVisibility', { isVisible: false })
                    }}>
                    {localize(messages.noResourceBubbleDescription)}
                </TeachingBubble>
            )}

            {!isSignedIn && !uiStore.languageStudioSignInTeachingBubbleHidden && (
                <TeachingBubble
                    hasCloseButton
                    target={`#${signInButtonId}`}
                    styles={{ footer: { justifyContent: 'center' } }}
                    headline={localize(messages.signInTeachingBubbleTitle)}
                    closeButtonAriaLabel={localize(messages.dismissButtonAria)}
                    calloutProps={{ preventDismissOnScroll: true, directionalHint: DirectionalHint.bottomAutoEdge }}
                    onDismiss={() => dispatcher.dispatch('updateLanguageStudioSignInTeachingBubbleVisibility', { isVisible: false })}
                    primaryButtonProps={{
                        children: localize(messages.signUp),
                        onClick: () => window.open('https://azure.microsoft.com/en-us/free')
                    }}>
                    {localize(messages.signInTeachingBubbleDescription)}
                    <TeachingBubbleLink target="_blank" href="https://azure.microsoft.com/en-us/free" $isLearnMore underline={false}>
                        {localize(messages.learnMoreAboutAzureAccount)}
                    </TeachingBubbleLink>
                    {localize(messages.signInTeachingBubbleDescription2)}&nbsp;
                    <TeachingBubbleLink target="_blank" href="https://azure.microsoft.com/en-us/free" underline={false}>
                        {localize(messages.createAzureAccount)}
                    </TeachingBubbleLink>
                    &nbsp;
                    {localize(messages.or)}&nbsp;
                    <TeachingBubbleLink target="_blank" href="https://aka.ms/learnmore-azure-account" underline={false}>
                        {localize(messages.learnHowToCreateAzureAccount)}
                    </TeachingBubbleLink>
                </TeachingBubble>
            )}
        </>
    );
});
